<template>
    <DsMultiSelect
        v-model="selectedClients"
        v-bind="$attrs"
        :id
        ref="clientMultiSelectRef"
        :disableSelectAll
        :label
        :options="clientOptions"
        :placeholder
        :required
    >
        <template #optionLabel="{ option }">
            <ClientMultiSelectOption
                :disabled="option.disabled"
                :email="option.email"
                :first="option.first"
                :last="option.last"
                :role="option.role"
            >
                <template #subtitle>
                    {{ option.phone ? `${option.email}, ${formatE164ToNational(option.phone)}` : option.email }}
                </template>
                <template #trailing>
                    <EditClientButton :clientId="option.value" />
                </template>
            </ClientMultiSelectOption>
        </template>

        <template #chipContent="{ option, truncate }">
            <span>
                {{ truncate(option.first && option.last ? joinFirstLast(option.first, option.last) : option.email) }}
            </span>
        </template>

        <template #emptyState>
            <DsSelectEmptyState
                v-if="!clientOptions.length"
                icon="fa-regular fa-user-group"
                subtitle="Once added, all clients will be listed here."
                title="No clients added yet"
            >
                <DsButton
                    leading="fa-regular fa-user-plus"
                    size="sm"
                    variant="tertiary"
                    @click="scrollToClientsSection"
                >
                    Add clients
                </DsButton>
            </DsSelectEmptyState>
        </template>
    </DsMultiSelect>
    <!-- @todo [CLENG-1942] remove hidden input needed for form submission -->
    <input
        v-for="client in selectedClients"
        :key="`${name}-${client.id}`"
        :name
        type="hidden"
        :value="client.value"
    />
</template>

<script setup lang="ts">
import type { ClientMultiSelectName } from '@/company-portal/add-file/selectors';
import { useAddFileFormScroller } from '@/company-portal/add-file/use-add-file-form-scroller';
import DsSelectEmptyState from '@/components/design-system/DsSelectEmptyState.vue';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import DsMultiSelect from '@/components/design-system/multiselect/DsMultiSelect.vue';
import EditClientButton from '@/components/Views/Company/AddFile/EditClient/EditClientButton.vue';
import { useClientMultiSelect } from '@/components/Views/Company/AddFile/useClientMultiSelect';
import ClientMultiSelectOption from '@/components/Views/Company/File/ClientMultiSelectOption.vue';
import { formatE164ToNational } from '@/shared/utils/phone-number';
import { joinFirstLast } from '@/shared/utils/strings';
import { useId, useTemplateRef } from 'vue';

export interface ClientMultiSelectProps {
    label?: string;
    name: ClientMultiSelectName;
    placeholder?: string;
    required?: boolean;
}

const props = withDefaults(defineProps<ClientMultiSelectProps>(), {
    label: 'Client(s)',
    placeholder: 'Select client(s)',
    required: true,
});

const fileFormScroller = useAddFileFormScroller();

const id = useId();

const { clientOptions, disableSelectAll, selectedClients } = useClientMultiSelect(props.name);

const clientMultiSelectRef = useTemplateRef('clientMultiSelectRef');

const scrollToClientsSection = () => {
    clientMultiSelectRef.value?.handleOpen(false);
    fileFormScroller.scrollToClientSection();
};
</script>
